import { cn } from '@/lib/utils'
import { CARD_SURFACE, darkenAccent } from '@/lib/menuCategoryStyle'

/** Compact accent-colored category button (counter + server menu). */
export function CategoryChip({
  name,
  count,
  accent,
  selected,
  onClick,
  categoryId,
}: {
  name: string
  count?: number
  accent: string
  selected: boolean
  onClick: () => void
  categoryId: string
}) {
  return (
    <button
      type="button"
      data-category-id={categoryId}
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'relative flex h-10 shrink-0 select-none items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-md border pl-4 pr-3 transition active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected ? 'text-white' : 'text-foreground',
      )}
      style={
        selected
          ? {
              // Computed in JS, NOT color-mix (F-FE-N152). This is the one
              // declaration the chip cannot afford to lose: with it dropped the
              // background is transparent, the border is transparent, and
              // text-white over a white pane leaves a full-height chip that
              // renders nothing. That is the "category disappeared and left a
              // gap" the tenant reported on their tablet's WebView.
              backgroundColor: darkenAccent(accent),
              borderColor: 'transparent',
            }
          : {
              // A wash, and purely cosmetic — an unselected chip keeps its
              // border and text-foreground, so losing this costs no legibility.
              // backgroundColor first as the plain fallback: if the color-mix
              // shorthand below is dropped, this one survives.
              backgroundColor: CARD_SURFACE,
              background: `color-mix(in srgb, ${accent} 12%, ${CARD_SURFACE})`,
              borderColor: `color-mix(in srgb, ${accent} 40%, transparent)`,
            }
      }
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1"
        // On the selected chip the stripe sits on the darkened accent, so a
        // translucent white reads. It must never be the ONLY thing carrying the
        // chip's shape — that is what made the failure invisible rather than ugly.
        style={{ background: selected ? 'rgba(255,255,255,0.45)' : accent }}
      />
      <span className="text-sm font-semibold leading-none">{name}</span>
      {typeof count === 'number' && (
        <span
          className={cn(
            'text-xs tabular-nums leading-none',
            selected ? 'text-white/80' : 'text-muted-foreground',
          )}
        >
          {count}
        </span>
      )}
    </button>
  )
}
